import useSWR, { mutate } from "swr";
import { GenerateUrlRequestBody, Paginated, PaginationQuery } from "../types";
import { buildUrl, useApi } from "./api";
import { CustomError } from "./errors";

export type AdminImageBody = {
  image: string | null;
  removeImage: boolean;
};

// The admin lists show the names of related entities (e.g. the screw family of a screw), so every
// mutation revalidates all the module's admin lists (the keys under moduleUrl), not only the mutated
// entity's one, and the keys starting with one of relatedUrls (e.g. the module's calculation settings).
export const mutateAdminCache = (moduleUrl: string, relatedUrls: readonly string[] = []) =>
  mutate(
    (key) =>
      typeof key === "string" && (key.startsWith(`${moduleUrl}/`) || relatedUrls.some((url) => key.startsWith(url))),
  );

// Filters of a list sent as their own query params (e.g. `deviceID`): the unset ones are left out.
export type AdminListParams = Record<string, string | number | null | undefined>;

const withListParams = (url: string, params: AdminListParams | undefined): string => {
  const searchParams = new URLSearchParams();
  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (value != null && value !== "") {
      searchParams.set(key, `${value}`);
    }
  });

  return searchParams.size > 0 ? `${url}?${searchParams.toString()}` : url;
};

// Calls shared by the admin entities of every module, served under `${moduleUrl}/${path}`: each entity
// hook exposes the subset its backend supports, under its own names.
export const createAdminCrudApi = <
  TSearchResponse extends Paginated<unknown>,
  TInsertBody = never,
  TUpdateBody = never,
>(
  api: ReturnType<typeof useApi>,
  moduleUrl: string,
  path: string,
  relatedUrls: readonly string[] = [],
) => {
  const url = `${moduleUrl}/${path}`;
  const revalidate = () => mutateAdminCache(moduleUrl, relatedUrls);

  const put = async <TBody>(itemUrl: string, body: TBody): Promise<void> => {
    await api.put<TBody>(itemUrl, body);
    revalidate();
  };

  const generateUploadImageUrl = (key: string, fileName: string, fileType: string): Promise<string> =>
    api.post<string, GenerateUrlRequestBody>(`${url}/${key}/image/generate_upload_url`, {
      filename: fileName,
      contentType: fileType,
    });

  return {
    useSearch: (query: PaginationQuery, signal: AbortSignal, params?: AdminListParams) =>
      useSWR<TSearchResponse, CustomError, string>(buildUrl(withListParams(url, params), query), (requestUrl) =>
        api.get<TSearchResponse>({ url: requestUrl, signal }),
      ),

    search: (
      query: PaginationQuery,
      signal: AbortSignal,
      params?: AdminListParams,
    ): Promise<TSearchResponse["items"]> =>
      api.get<TSearchResponse>({ url: buildUrl(withListParams(url, params), query), signal }).then((x) => x.items),

    insert: async (body: TInsertBody): Promise<{ id: string }> => {
      const result = await api.post<{ id: string }, TInsertBody>(url, body);
      revalidate();
      return result;
    },

    update: (key: string, body: TUpdateBody) => put(`${url}/${key}`, body),

    remove: async (key: string): Promise<void> => {
      await api.del(`${url}/${key}`);
      revalidate();
    },

    updateVisibility: (key: string, body: { visibility: string }) => put(`${url}/${key}/visibility`, body),

    updateOrder: (key: string, body: { order: number }) => put(`${url}/${key}/order`, body),

    generateUploadImageUrl,

    // Uploads the image picked in the form (if any) and returns the image fields of the update body.
    uploadImage: async (key: string, newImage: File | undefined, removeImage: boolean): Promise<AdminImageBody> => {
      if (!newImage) {
        return { image: null, removeImage };
      }

      const signedUrl = await generateUploadImageUrl(key, newImage.name, newImage.type);
      const res = await api.uploadFile(signedUrl, newImage);
      if (!res.ok) {
        throw res;
      }

      return { image: newImage.name, removeImage: false };
    },
  };
};
